import React, { useState, useRef } from 'react';
import MergeTagSelector from './MergeTagSelector';
import { PersonalizationService } from '../../services/personalizationService';
import aiEmailService from '../../services/aiEmailService';

interface TemplateEditorProps {
  subject: string;
  content: string;
  customFields?: any[];
  onSubjectChange: (subject: string) => void;
  onContentChange: (content: string) => void;
  className?: string;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  subject,
  content,
  customFields = [],
  onSubjectChange,
  onContentChange,
  className = ''
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<Record<string, any>>({
    firstName: 'John',
    lastName: 'Doe',
    company: 'Acme Inc',
    position: 'Marketing Manager',
    email: 'john.doe@example.com',
    currentDate: new Date().toLocaleDateString(),
    currentTime: new Date().toLocaleTimeString(),
    currentDateTime: new Date().toLocaleString(),
  });
  
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [tone, setTone] = useState<'concise'|'friendly'|'formal'|'persuasive'|'neutral'>('concise');
  
  const handleTagInsert = (tag: string) => {
    if (contentTextareaRef.current) {
      const textarea = contentTextareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      const newContent = content.substring(0, start) + tag + content.substring(end);
      onContentChange(newContent);
      
      // Set focus back to textarea
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(start + tag.length, start + tag.length);
        }
      }, 0);
    }
  };
  
  const handlePreviewDataChange = (field: string, value: string) => {
    setPreviewData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleValidateTemplate = () => {
    const result = PersonalizationService.validateTemplate(content);
    
    if (result.isValid) {
      alert('Template is valid!');
    } else {
      alert(`Template has errors:\n${result.errors.join('\n')}`);
    }
  };
  
  const previewContent = PersonalizationService.previewTemplate(content, previewData);

  const handlePersonalizeAI = async () => {
    try {
      setAiError(null);
      setAiLoading(true);
      const lead = {
        firstName: previewData.firstName,
        lastName: previewData.lastName,
        company: previewData.company,
        role: previewData.position,
        email: previewData.email,
        // space for optional fields, if present
      };
      const res = await aiEmailService.personalize(content, lead, 'Cold email outreach');
      if (res?.text) onContentChange(res.text);
    } catch (e: any) {
      const msg = e?.message || 'Failed to personalize with AI (premium required)';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRewriteAI = async () => {
    try {
      setAiError(null);
      setAiLoading(true);
      const res = await aiEmailService.rewrite(content, tone, 120);
      if (res?.text) onContentChange(res.text);
    } catch (e: any) {
      const msg = e?.message || 'Failed to rewrite with AI (premium required)';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };
  
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Subject Line */}
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
          Subject Line
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            className="focus:ring-blue-500 focus:border-blue-500 block w-full pr-24 sm:text-sm border-gray-300 rounded-md"
            placeholder="Enter subject line"
          />
          <div className="absolute inset-y-0 right-0 flex items-center">
            <MergeTagSelector
              customFields={customFields}
              onTagSelect={handleTagInsert}
              className="mr-1"
            />
          </div>
        </div>
      </div>
      
      {/* Content Editor */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <label htmlFor="content" className="block text-sm font-medium text-gray-700">
            Email Content
          </label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isPreviewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleValidateTemplate}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Validate
            </button>
            {/* AI Actions */}
            <div className="flex items-center gap-2">
              <select
                aria-label="AI tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as any)}
                className="px-2 py-1 border border-gray-300 rounded text-xs"
              >
                <option value="concise">Concise</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="persuasive">Persuasive</option>
                <option value="neutral">Neutral</option>
              </select>
              <button
                type="button"
                onClick={handlePersonalizeAI}
                disabled={aiLoading}
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
                title="Personalize this template for the current preview contact"
              >
                {aiLoading ? 'Personalizing…' : 'Personalize (AI)'}
              </button>
              <button
                type="button"
                onClick={handleRewriteAI}
                disabled={aiLoading}
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
                title="Rewrite for selected tone"
              >
                {aiLoading ? 'Rewriting…' : 'Rewrite (AI)'}
              </button>
            </div>
            <MergeTagSelector
              customFields={customFields}
              onTagSelect={handleTagInsert}
            />
          </div>
        </div>
        {aiError && (
          <div className="mb-2 text-xs text-red-600">{aiError}</div>
        )}
        
        {isPreviewMode ? (
          <div className="mt-1 bg-white border border-gray-300 rounded-md p-4 min-h-[200px] max-h-[400px] overflow-y-auto">
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: previewContent.replace(/\n/g, '<br />') }} />
          </div>
        ) : (
          <textarea
            ref={contentTextareaRef}
            id="content"
            rows={12}
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholder="Enter email content"
          />
        )}
      </div>
      
      {/* Preview Data Editor */}
      {isPreviewMode && (
        <div className="bg-gray-50 p-4 rounded-md">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Preview Data</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(previewData).map(([key, value]) => (
              <div key={key}>
                <label htmlFor={`preview-${key}`} className="block text-xs font-medium text-gray-700 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <input
                  type="text"
                  id={`preview-${key}`}
                  value={value}
                  onChange={(e) => handlePreviewDataChange(key, e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
